import { useEffect, useMemo, useRef } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Spinners from "../../components/Common/Spinner";
import { useGetClientBookings } from "../../api/booking";
import { useUserMe } from "../../api/users";
import { selectUser } from "../../store/auth/selectors";
import { isUserActionRequired } from "../../helpers/userStatusHelper";
import {
  extractClientBookingsList,
  getClientCompanySummary,
  getClientDashboardStatus,
  getClientDisplayName,
} from "./clientDashboardHelpers";
import ClientDashboardHero from "./components/ClientDashboardHero";
import ClientStatusStrip from "./components/ClientStatusStrip";
import ClientCompanyCard from "./components/ClientCompanyCard";
import ClientRecentBookings from "./components/ClientRecentBookings";

const RECENT_BOOKINGS_LIMIT = 5;

const ClientDashboard = ({ t }) => {
  document.title = t("clientDashboardPageTitle", "Dashboard | Client");

  const authUser = useSelector(selectUser);
  const actionRequired = isUserActionRequired(authUser);

  const userMe = useUserMe();
  const bookingsQuery = useGetClientBookings();

  const raw = useMemo(
    () => userMe?.data?.data ?? userMe?.data ?? {},
    [userMe.data]
  );

  const displayName = useMemo(
    () =>
      getClientDisplayName(raw) ||
      t("clientDashboardDefaultName", "Client"),
    [raw, t]
  );
  const dashboardStatus = useMemo(
    () => getClientDashboardStatus(raw),
    [raw]
  );
  const actionRequiredReason =
    typeof raw.reason === "string" ? raw.reason.trim() : "";
  const companySummary = useMemo(
    () => getClientCompanySummary(raw),
    [raw]
  );

  const lastFetchKeyRef = useRef("");
  useEffect(() => {
    if (actionRequired) return;
    const fetchKey = `recent-${RECENT_BOOKINGS_LIMIT}`;
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    bookingsQuery.mutate({ page: 1, limit: RECENT_BOOKINGS_LIMIT });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionRequired]);

  const recentBookings = useMemo(
    () => extractClientBookingsList(bookingsQuery.data).slice(0, RECENT_BOOKINGS_LIMIT),
    [bookingsQuery.data]
  );

  const isProfileLoading = userMe.isLoading || userMe.isPending;

  return (
    <div className="page-content">
      <div className="container-fluid">
        {isProfileLoading ? (
          <Spinners setLoading={() => {}} />
        ) : (
          <>
            <ClientDashboardHero
              displayName={displayName}
              isApproved={dashboardStatus.isApproved}
              showRequestStaff={!actionRequired}
            />
            <ClientStatusStrip
              status={dashboardStatus}
              reason={actionRequiredReason}
            />
            <ClientCompanyCard summary={companySummary} />
            {!actionRequired && (
              <ClientRecentBookings
                bookings={recentBookings}
                loading={bookingsQuery.isLoading || bookingsQuery.isPending}
                error={bookingsQuery.error}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default withTranslation()(ClientDashboard);
